import { useAuth, useAuthGate } from '@frontend/auth';
import { AuthErrorState, AuthStatusMessage, SignedOutState } from '@frontend/ui';
import { Outlet, useLocation } from 'react-router';

/**
 * Layout guard for authenticated-only routes — the legacy portal's
 * `RequireAuthentication` shape on the modern seam. `useAuthGate` owns the
 * redirect (effect-based, destination-preserving); this component only
 * renders the coarse states. A deliberately signed-out tab gets the
 * signed-out screen and must NOT auto-login (see `AuthStatus`). A terminal
 * `'error'` (e.g. a token that fails profile parsing) renders a retry surface
 * here too, before the `<Outlet />` fall-through, so it never leaks protected
 * content.
 */
export function RequireAuthentication() {
  const { isLoading, isUnauthed, isSignedOut, isError } = useAuthGate();
  const { login } = useAuth();
  const { pathname, search } = useLocation();

  if (isLoading) {
    return <AuthStatusMessage>Checking your session…</AuthStatusMessage>;
  }

  if (isSignedOut) {
    return (
      <SignedOutState
        onLogin={() => {
          void login();
        }}
      />
    );
  }

  if (isError) {
    // The guard's location is always a real protected route (never
    // /login), so preserving it as returnTo carries no cycle risk and
    // lands the user back where they were after re-auth.
    return (
      <AuthErrorState
        onRetry={() => {
          void login({ returnTo: `${pathname}${search}` });
        }}
      />
    );
  }

  if (isUnauthed) {
    // The gate's effect is already redirecting to login.
    return <AuthStatusMessage>Redirecting to sign in…</AuthStatusMessage>;
  }

  return <Outlet />;
}
